import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle, XCircle, Eye, Calculator, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface UnitMeasurementFlag {
  id: string;
  hospital_id: string;
  procedure_code: string;
  flag_type: string;
  severity: string;
  original_price: number;
  median_price: number;
  price_deviation_percentage: number;
  suspected_unit_factor: number;
  status: string;
  created_at: string;
  hospital_name?: string;
}

interface NormalizationRule {
  id: string;
  procedure_code: string;
  hospital_id: string;
  normalization_factor: number;
  standard_unit_size: number;
  original_unit_size: number;
  confidence_score: number;
  status: string;
  notes: string;
  detection_method: string;
  created_by?: string;
  hospital_name?: string;
}

export function UnitNormalizationManager() {
  const [selectedTab, setSelectedTab] = useState<'flags' | 'rules'>('flags');
  const [createRuleDialog, setCreateRuleDialog] = useState<UnitMeasurementFlag | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch flagged records
  const { data: flaggedRecords = [], isLoading: flagsLoading } = useQuery({
    queryKey: ['unit-measurement-flags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unit_measurement_flags')
        .select(`
          *,
          hospitals!hospital_id(name)
        `)
        .eq('status', 'flagged')
        .order('price_deviation_percentage', { ascending: false });
      
      if (error) throw error;
      
      return data.map(flag => ({
        ...flag,
        hospital_name: flag.hospitals?.name || 'Unknown Hospital'
      }));
    }
  });

  // Fetch normalization rules
  const { data: normalizationRules = [], isLoading: rulesLoading } = useQuery({
    queryKey: ['normalization-rules'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unit_normalization_rules')
        .select(`
          *,
          hospitals!hospital_id(name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data.map(rule => ({
        ...rule,
        hospital_name: rule.hospitals?.name || 'Unknown Hospital'
      }));
    }
  });

  // Run outlier detection
  const runDetectionMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('flag_unit_measurement_outliers');
      if (error) throw error;
      return data;
    },
    onSuccess: (flagsCreated) => {
      queryClient.invalidateQueries({ queryKey: ['unit-measurement-flags'] });
      toast({
        title: "Detection Complete",
        description: `Found and flagged ${flagsCreated} potential unit measurement issues`,
      });
    },
    onError: (error) => {
      toast({
        title: "Detection Failed", 
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Create normalization rule
  const createRuleMutation = useMutation({
    mutationFn: async (ruleData: Omit<NormalizationRule, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('unit_normalization_rules')
        .insert(ruleData)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['normalization-rules'] });
      queryClient.invalidateQueries({ queryKey: ['unit-measurement-flags'] });
      setCreateRuleDialog(null);
      toast({
        title: "Rule Created",
        description: "Normalization rule has been created successfully",
      });
    }
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-300';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Unit Measurement Normalization</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => runDetectionMutation.mutate()}
            disabled={runDetectionMutation.isPending}
            variant="outline"
          >
            {runDetectionMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4 mr-2" />
            )}
            Run Detection
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <div className="flex space-x-8">
          <button
            onClick={() => setSelectedTab('flags')}
            className={`py-2 px-1 border-b-2 font-medium transition-colors ${
              selectedTab === 'flags'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Flagged Records ({flaggedRecords.length})
          </button>
          <button
            onClick={() => setSelectedTab('rules')}
            className={`py-2 px-1 border-b-2 font-medium transition-colors ${
              selectedTab === 'rules'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Normalization Rules ({normalizationRules.length})
          </button>
        </div>
      </div>

      {/* Flagged Records Tab */}
      {selectedTab === 'flags' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning-amber" />
              Unit Measurement Issues Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hospital</TableHead>
                    <TableHead>Procedure</TableHead>
                    <TableHead>Original Price</TableHead>
                    <TableHead>Median Price</TableHead>
                    <TableHead>Deviation</TableHead>
                    <TableHead>Suggested Factor</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flagsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : flaggedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No flagged records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    flaggedRecords.map((flag) => (
                      <TableRow key={flag.id}>
                        <TableCell className="max-w-xs truncate">
                          {flag.hospital_name}
                        </TableCell>
                        <TableCell className="font-mono">
                          {flag.procedure_code}
                        </TableCell>
                        <TableCell className="font-mono">
                          ${flag.original_price?.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono">
                          ${flag.median_price?.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <span className="text-red-500 font-bold">
                            -{flag.price_deviation_percentage?.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="font-mono">
                          {flag.suspected_unit_factor?.toFixed(1)}x
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getSeverityColor(flag.severity)}>
                            {flag.severity.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => setCreateRuleDialog(flag)}
                            className="mr-2"
                          >
                            Create Rule
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Normalization Rules Tab */}
      {selectedTab === 'rules' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Active Normalization Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hospital</TableHead>
                    <TableHead>Procedure</TableHead>
                    <TableHead>Factor</TableHead>
                    <TableHead>Standard Unit</TableHead>
                    <TableHead>Original Unit</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rulesLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : normalizationRules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No normalization rules found
                      </TableCell>
                    </TableRow>
                  ) : (
                    normalizationRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="max-w-xs truncate">
                          {rule.hospital_name}
                        </TableCell>
                        <TableCell className="font-mono">
                          {rule.procedure_code}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-primary">
                          {rule.normalization_factor}x
                        </TableCell>
                        <TableCell className="font-mono">
                          {rule.standard_unit_size}
                        </TableCell>
                        <TableCell className="font-mono">
                          {rule.original_unit_size}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            rule.confidence_score >= 90 ? 'bg-green-100 text-green-800 border-green-300' :
                            rule.confidence_score >= 70 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                            'bg-red-100 text-red-800 border-red-300'
                          }>
                            {rule.confidence_score}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusColor(rule.status)}>
                            {rule.status.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {rule.notes}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Rule Dialog */}
      <Dialog open={!!createRuleDialog} onOpenChange={() => setCreateRuleDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Normalization Rule</DialogTitle>
          </DialogHeader>
          {createRuleDialog && (
            <CreateNormalizationRuleForm
              flag={createRuleDialog}
              onSubmit={(ruleData) => createRuleMutation.mutate({
                ...ruleData,
                status: ruleData.status || 'pending',
                hospital_id: ruleData.hospital_id || createRuleDialog.hospital_id || '',
                procedure_code: ruleData.procedure_code || createRuleDialog.procedure_code || '',
                notes: ruleData.notes || '',
                confidence_score: ruleData.confidence_score || 0.5,
                detection_method: ruleData.detection_method || 'manual',
                normalization_factor: ruleData.normalization_factor || 1.0,
                standard_unit_size: ruleData.standard_unit_size || 1.0,
                original_unit_size: ruleData.original_unit_size || 1.0
              })}
              isSubmitting={createRuleMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Form component for creating normalization rules
function CreateNormalizationRuleForm({
  flag,
  onSubmit,
  isSubmitting
}: {
  flag: UnitMeasurementFlag;
  onSubmit: (data: Partial<NormalizationRule>) => void;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState({
    normalization_factor: flag.suspected_unit_factor || 1,
    standard_unit_size: flag.median_price || 1,
    original_unit_size: flag.original_price || 1,
    confidence_score: 80,
    status: 'pending' as 'pending' | 'approved' | 'rejected',
    notes: `Auto-detected unit measurement discrepancy. Original price ${flag.original_price} is ${flag.price_deviation_percentage.toFixed(1)}% below median ${flag.median_price}.`
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      procedure_code: flag.procedure_code,
      hospital_id: flag.hospital_id,
      detection_method: 'manual_analysis',
      created_by: 'analyst',
      ...formData
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Hospital</label>
          <Input value={flag.hospital_name} disabled />
        </div>
        <div>
          <label className="text-sm font-medium">Procedure Code</label>
          <Input value={flag.procedure_code} disabled />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-medium">Normalization Factor</label>
          <Input
            type="number"
            step="0.1"
            value={formData.normalization_factor}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              normalization_factor: parseFloat(e.target.value) || 1
            }))}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Standard Unit Size</label>
          <Input
            type="number"
            step="0.1"
            value={formData.standard_unit_size}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              standard_unit_size: parseFloat(e.target.value) || 1
            }))}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Original Unit Size</label>
          <Input
            type="number"
            step="0.1"
            value={formData.original_unit_size}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              original_unit_size: parseFloat(e.target.value) || 1
            }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Confidence Score (0-100)</label>
          <Input
            type="number"
            min="0"
            max="100"
            value={formData.confidence_score}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              confidence_score: parseInt(e.target.value) || 0
            }))}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Status</label>
          <Select
            value={formData.status}
            onValueChange={(value: 'pending' | 'approved' | 'rejected') => 
              setFormData(prev => ({ ...prev, status: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Notes</label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          rows={3}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={() => {}}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Rule'
          )}
        </Button>
      </div>
    </form>
  );
}